/**
 * Messages - Production-grade messaging interface
 * Responsive two-panel layout inspired by clean marketplace chat UX
 * Supports product chats, direct/admin chats, offers, location sharing
 * Mobile-first, dark mode consistent, scalable for future order/shipping flows
 */
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Trash2 } from 'lucide-react';
import useAuth from '../hooks/useAuth';
import useOfferHandlers from '../hooks/useOfferHandlers';
import MessageInput from '../components/MessageInput';
import OfferModal from '../components/OfferModal';
import BuyNowGuidanceModal from '../components/BuyNowGuidanceModal';
import OfferBubbleClean from '../components/chat/OfferBubble';
import PaymentRequestBubble from '../components/chat/PaymentRequestBubble';
import MessagingLayout from '../components/chat/MessagingLayout';
import ConversationsSidebar from '../components/chat/ConversationsSidebar';
import ProductHeader from '../components/chat/ProductHeader';
  import ConfirmDialog from '../components/ui/ConfirmDialog';
import * as offersService from '../services/offersService';
import { userRepository } from '../repositories/userRepository';
import { productRepository } from '../repositories/productRepository';
import { errorDev } from '../utils/logger.js';
import {
   findOrCreateChat,
   getChatMeta,
  listenMessages,
  markChatRead,
  sendMessage,
  setTyping,
  deleteMessageForUser,
  deleteConversationForUser,
} from '../services/chatService';

function stringifyText(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (v && typeof v === 'object') {
    try { return JSON.stringify(v); } catch (err) { errorDev("Operation failed:", err);  return String(v); }
  }
  return String(v);
}

function formatMessageTime(timestamp) {
  if (!timestamp) return '';
  try {
    const date = typeof timestamp?.toDate === 'function'
      ? timestamp.toDate()
      : timestamp?.seconds
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (err) {
    errorDev("Operation failed:", err);
    return '';
  }
}

function formatMessageDate(timestamp) {
  if (!timestamp) return '';
  try {
    const date = typeof timestamp?.toDate === 'function'
      ? timestamp.toDate()
      : timestamp?.seconds
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (msgDate.getTime() === today.getTime()) return 'Today';
    if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch (err) {
    errorDev("Operation failed:", err);
    return '';
  }
}

function deriveOfferUiStatus(msg, offerDoc) {
  const explicit = String(msg?.extra?.offerStatus || msg?.offerStatus || "").trim().toLowerCase();
  const dealStatus = String(offerDoc?.dealStatus || "").trim().toLowerCase();
  const baseStatus = String(offerDoc?.status || explicit || "pending").trim().toLowerCase();
  const sent = Boolean(offerDoc?.deliverySentAt || msg?.extra?.deliverySentAt);
  const received = Boolean(offerDoc?.deliveryReceivedAt || msg?.extra?.deliveryReceivedAt);

  if (received || dealStatus.includes("complet") || dealStatus.includes("receiv")) return "completed";
  if (sent || dealStatus.includes("sent") || dealStatus.includes("ship") || dealStatus.includes("transit")) {
    return "accepted";
  }
  if (baseStatus.includes("declin") || baseStatus.includes("cancel") || baseStatus.includes("reject")) {
    return "declined";
  }
  if (baseStatus.includes("accept") || dealStatus.includes("accept") || dealStatus.includes("approv")) {
    return "accepted";
  }
  return explicit || "pending";
}

function resolveSystemMessageText(msg, t) {
  const action = String(msg?.extra?.action || "").trim().toLowerCase();
  const raw = stringifyText(msg?.text).trim();

  if (action === 'sent' || raw.toLowerCase().startsWith('seller has sent the item')) {
    return t('inbox:sellerSentSystemMessage', 'Seller has sent the item. Please confirm when it arrives.');
  }

  if (action === 'received' || raw.toLowerCase().includes('transaction completed')) {
    return t('inbox:buyerReceivedSystemMessage', 'Buyer confirmed the item was received. Transaction completed.');
  }

  return raw;
}

export default function Messages() {
  const { chatId: routeChatId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['inbox', 'common']);
  const { user: currentUser } = useAuth();

  const [chatId, setChatId] = useState(routeChatId || searchParams.get('chatId') || null);
  const [chat, setChat] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [product, setProduct] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageLoadError, setMessageLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [counterToOfferId, setCounterToOfferId] = useState(null);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const [offerCache, setOfferCache] = useState({});
  const [notice, setNotice] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDeletingConversation, setIsDeletingConversation] = useState(false);
  const [deletingMessageIds, setDeletingMessageIds] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    onConfirm: null,
    loading: false,
  });
  const bottomRef = useRef(null);
  const previousMessageCountRef = useRef(0);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const { handleAcceptOffer, handleDeclineOffer, onPaymentSuccess } = useOfferHandlers(
    currentUser,
    chatId,
    messages,
    setMessages,
    offerCache,
    setOfferCache
  );

  // Sync route -> chatId
  useEffect(() => {
    if (routeChatId && routeChatId !== chatId) setChatId(routeChatId);
  }, [routeChatId]);

  // Create/find chat if needed
  useEffect(() => {
    (async () => {
      if (!currentUser || chatId) return;

      const explicitCid = searchParams.get('chatId');
      if (explicitCid) {
        setChatId(explicitCid);
        navigate(`/messages/${encodeURIComponent(explicitCid)}`, { replace: true });
        return;
      }

      const recipientId = searchParams.get('recipientId');
      const productId = searchParams.get('productId');

      if (!recipientId) {
        setLoading(false);
        return;
      }

      try {
        let cid;
        const result = await findOrCreateChat(currentUser.uid, recipientId, productId || null);
        cid = result.chatId;
        setChatId(cid);
        navigate(`/messages/${encodeURIComponent(cid)}`, { replace: true });
      } catch (err) {
        errorDev("Operation failed:", err);
        setLoading(false);
      }
    })();
  }, [chatId, currentUser, navigate, searchParams]);

  // Load chat meta / recipient / context item
  useEffect(() => {
    (async () => {
      if (!chatId || !currentUser) return;
      setLoading(true);
      try {
        const meta = await getChatMeta(chatId);
        setChat(meta);

        const otherId = (meta?.participants || []).find((id) => id !== currentUser.uid);
        if (otherId) {
          try {
            // Route through userRepository so this page shares one
            // profile-read definition with the rest of the app.
            const recipientProfile = await userRepository.getById(otherId);
            if (recipientProfile) setRecipient({ ...recipientProfile });
          } catch (err) { errorDev("Operation failed:", err);}
        }

        if (meta?.productId) {
          try {
            // Route through productRepository so this page shares one
            // product-read definition with the rest of the app.
            const productRow = await productRepository.getById(meta.productId);
            if (productRow) setProduct({ ...productRow });
          } catch (err) { errorDev("Operation failed:", err);}
        } else {
          setProduct(null);
        }
      } catch (err) {
        // silent
      errorDev("Operation failed:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [chatId, currentUser]);

  // Listen for messages
  useEffect(() => {
    if (!chatId || !currentUser) return;
    setMessageLoadError(null);
    const unsub = listenMessages(chatId, (msgs) => {
      setMessages(msgs);
      setMessageLoadError(null);
      try { markChatRead(chatId, currentUser.uid); } catch (err) { errorDev("Operation failed:", err);}
    }, (err) => {
      errorDev('Message listener error:', err);
      setMessageLoadError(err);
      setLoading(false);
    });
    return () => { unsub && unsub(); };
  }, [chatId, currentUser]);

  // Auto-scroll
  useEffect(() => {
    const previousCount = previousMessageCountRef.current;
    const nextBehavior = messages.length > previousCount && previousCount > 0 ? 'smooth' : 'auto';
    bottomRef.current?.scrollIntoView({ behavior: nextBehavior });
    previousMessageCountRef.current = messages.length;
  }, [messages]);

  // Preload offer docs
  useEffect(() => {
    let mounted = true;
    const toLoad = new Set();
    for (const m of messages) {
      const oid = m?.extra?.offerId;
      if (oid && !offerCache[oid]) toLoad.add(oid);
    }
    if (toLoad.size === 0) return;

    (async () => {
      const updates = {};
      for (const id of toLoad) {
        try { updates[id] = (await offersService.getOfferById(id)) || null; }
        catch (err) { errorDev("Operation failed:", err);  updates[id] = null; }
      }
      if (mounted) setOfferCache((prev) => ({ ...prev, ...updates }));
    })();

    return () => { mounted = false; };
  }, [messages, offerCache]);

  const otherTyping = chat?.typing && Object.entries(chat.typing).some(([uid, val]) => uid !== currentUser.uid && !!val);

  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentDate = null;
    for (const msg of messages) {
      const dateLabel = formatMessageDate(msg.createdAt || msg.timestamp);
      if (dateLabel !== currentDate) {
        currentDate = dateLabel;
        groups.push({ type: 'date', label: dateLabel });
      }
      groups.push({ type: 'message', data: msg });
    }
    return groups;
  }, [messages]);

  const isSupportChatForCurrentUser = chat?.contextType === 'support'
    && String(chat?.supportUserId || '') === String(currentUser?.uid || '');

  // **FIX 3:** Back handler — on mobile toggle sidebar overlay, on desktop stay put
  const handleBack = useCallback(() => {
    // On mobile, toggle the conversation sidebar overlay instead of navigating away
    // This keeps the chat mounted so user can return to it instantly
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (isMobile) {
      setSidebarOpen((prev) => !prev);
    }
  }, []);

  async function handleSend(text) {
    if (!chatId || !currentUser) return;
    try {
      setNotice('');
      await sendMessage({ chatId, senderId: currentUser.uid, text, type: 'text', extra: {} });
    } catch (err) {
      errorDev("Operation failed:", err);
      setNotice("It's Orrith, not you. We could not send that message. Please try again.");
    }
  }

  async function handleTypingChange(isTyping) {
    if (!chatId || !currentUser) return;
    try { await setTyping(chatId, currentUser.uid, isTyping); } catch (err) { errorDev("Operation failed:", err);}
  }

  function handleMakeOffer() {
    if (!product) {
      setNotice("It's Orrith, not you. Product details are still loading.");
      return;
    }
    setCounterToOfferId(null);
    setShowOfferModal(true);
  }

  const handleDeleteConversation = useCallback(async () => {
    if (!chatId || !currentUser?.uid || isDeletingConversation) return;
    setConfirmDialog({
      open: true,
      title: t('inbox:deleteConversation', 'Delete conversation'),
      message: t('inbox:deleteConversationConfirm', 'Delete this conversation from your inbox?'),
      confirmLabel: t('inbox:delete', 'Delete'),
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        setIsDeletingConversation(true);
        try {
          await deleteConversationForUser({ chatId, userId: currentUser.uid });
          setConfirmDialog({ open: false, title: '', message: '', confirmLabel: 'Confirm', onConfirm: null, loading: false });
          navigate('/messages');
        } catch (err) {
          errorDev("Operation failed:", err);
          setNotice("It's Orrith, not you. We could not delete this conversation right now.");
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        } finally {
          setIsDeletingConversation(false);
        }
      },
    });
  }, [chatId, currentUser?.uid, isDeletingConversation, navigate, t]);

  const handleDeleteMessage = useCallback(async (messageId) => {
    if (!chatId || !currentUser?.uid || !messageId) return;
    setConfirmDialog({
      open: true,
      title: t('inbox:deleteMessage', 'Delete message'),
      message: t('inbox:deleteMessageConfirm', 'Delete this message?'),
      confirmLabel: t('inbox:delete', 'Delete'),
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        setDeletingMessageIds((prev) => ({ ...prev, [messageId]: true }));
        try {
          await deleteMessageForUser({ chatId, messageId, userId: currentUser.uid });
          setConfirmDialog({ open: false, title: '', message: '', confirmLabel: 'Confirm', onConfirm: null, loading: false });
        } catch (err) {
          errorDev("Operation failed:", err);
          setNotice("It's Orrith, not you. We could not delete this message right now.");
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        } finally {
          setDeletingMessageIds((prev) => ({ ...prev, [messageId]: false }));
        }
      },
    });
  }, [chatId, currentUser?.uid, t]);

  function handleBuyNow() {
    if (!product) {
      setNotice("It's Orrith, not you. Product details are still loading.");
      return;
    }
    setShowBuyNowModal(true);
  }

  // **FIX 4:** Render default header with a back button always visible
  const renderDefaultHeader = (onBack) => (
    <div className="px-3 py-2 bg-card flex items-center gap-2">
      <button
        onClick={onBack}
        className="p-1 -ml-1 rounded-full hover:bg-muted transition-colors"
        aria-label="Back to conversations"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-foreground truncate">
          {isSupportChatForCurrentUser
            ? t('inbox:supportCenterTitle', 'Support Center')
            : (recipient ? (recipient.displayName || recipient.name || recipient.email?.split('@')[0]) : 'User')}
        </div>
        <div className="text-xs text-muted-foreground">
          {isSupportChatForCurrentUser
            ? t('inbox:supportConversation', 'Support Conversation')
            : t('inbox:directMessage', 'Direct Message')}
        </div>
      </div>
    </div>
  );

  const renderMessageBubble = (msg) => {
    const mine = String(msg.senderId) === String(currentUser.uid);
    const isDeleted = msg?.isDeleted === true;
    const offerId = msg?.extra?.offerId || msg?.offerId;
    const offerDoc = offerCache[offerId];
    const status = deriveOfferUiStatus(msg, offerDoc);

    const canAccept = msg.type === 'offer' && !mine && status === 'pending' && offerDoc?.sellerId === currentUser.uid;
    const canDecline = msg.type === 'offer' && !mine && status === 'pending' && offerDoc?.sellerId === currentUser.uid;

    if (msg.type === 'offer' && !isDeleted) {
      return (
        <OfferBubbleClean
          msg={msg}
          currentUser={currentUser}
          isSender={mine}
          align={mine ? 'right' : 'left'}
          offerDoc={offerDoc}
          status={status}
          onAccept={canAccept ? () => handleAcceptOffer(msg) : undefined}
          onDecline={canDecline ? () => handleDeclineOffer(msg) : undefined}
          onCounterOffer={product ? () => {
            const otherPartyId = offerDoc?.sellerId === currentUser.uid
              ? offerDoc?.buyerId
              : offerDoc?.sellerId;
            setCounterToOfferId(offerId || null);
            setShowOfferModal(true);
          } : undefined}
        />
      );
    }

    if (msg.type === 'payment_request' && !isDeleted) {
      return (
        <PaymentRequestBubble
          msg={msg}
          currentUser={currentUser}
          isSender={mine}
          align={mine ? 'right' : 'left'}
          onPaymentSuccess={onPaymentSuccess}
          chatId={chatId}
        />
      );
    }

    if (msg.type === 'location' && !isDeleted) {
      const loc = msg.extra?.location;
      const hasCoords = loc?.lat && loc?.lng;
      const sharedBy = msg.extra?.sharedBy || '';
      const mapsUrl = hasCoords ? `https://www.google.com/maps?q=${loc.lat},${loc.lng}` : null;
      if (hasCoords) {
        return (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`block max-w-[280px] rounded-2xl overflow-hidden border shadow-sm transition hover:shadow-md ${mine ? 'border-green-500/20' : 'border-border'} ${mine ? 'bg-green-600 text-white' : 'bg-card text-foreground'}`}
          >
            <div className="px-3 py-3">
              <div className="text-sm font-semibold truncate">
                {sharedBy ? `${sharedBy}'s location` : t('inbox:locationMessage', 'Shared location')}
              </div>
              <p className={`text-xs mt-1 ${mine ? 'text-white/80' : 'text-muted-foreground'}`}>
                {Number(loc.lat).toFixed(5)}, {Number(loc.lng).toFixed(5)}
              </p>
              <div className={`text-xs font-medium mt-2 ${mine ? 'text-white' : 'text-green-600 dark:text-green-400'}`}>
                Open in Google Maps
              </div>
            </div>
          </a>
        );
      }
    }

    if (msg.type === 'system' && !isDeleted) {
      return (
        <div className="max-w-[88vw] sm:max-w-md rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-100 dark:bg-neutral-800/80 px-3 py-2.5">
          <p className="text-sm leading-snug text-gray-700 dark:text-gray-200">
            {resolveSystemMessageText(msg, t)}
          </p>
          <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            {formatMessageTime(msg.createdAt || msg.timestamp)}
          </div>
        </div>
      );
    }

    return (
      <div className="relative group">
        <div
          className={`max-w-[82vw] sm:max-w-sm rounded-2xl px-3 py-2.5 text-sm break-words shadow-sm ${
            mine
              ? 'bg-green-600 text-white rounded-br-md'
              : 'bg-card border border-border text-foreground rounded-bl-md'
          } ${isDeleted ? 'opacity-70 italic' : ''}`}
        >
          {isDeleted ? t('inbox:deletedMessage', 'This message was deleted.') : stringifyText(msg.text)}
          <div className={`mt-1 text-[10px] ${mine ? 'text-white/70' : 'text-muted-foreground'}`}>
            {formatMessageTime(msg.createdAt || msg.timestamp)}
          </div>
        </div>

        {mine && !isDeleted && (
          <button
            type="button"
            onClick={() => handleDeleteMessage(msg.id)}
            disabled={Boolean(deletingMessageIds[msg.id])}
            className="absolute -top-2 -right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white dark:bg-neutral-800 border border-border text-red-600 dark:text-red-400 disabled:opacity-50"
            aria-label={t('inbox:deleteMessage', 'Delete message')}
            title={t('inbox:deleteMessage', 'Delete message')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center h-[calc(100vh-140px)]">
        <div className="text-center px-6">
          <p className="text-muted-foreground mb-4">{t('inbox:loginToMessage', 'Please log in to message')}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            {t('inbox:goToLogin', 'Go to Login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MessagingLayout
        hasChatSelected={!!chatId}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        sidebarContent={
          <ConversationsSidebar
            onSelectConversation={(cid) => navigate(`/messages/${encodeURIComponent(cid)}`)}
            selectedChatId={chatId}
          />
        }
        headerContent={
          chatId ? (
            product ? (
              <ProductHeader
                product={product}
                seller={recipient}
                onBack={handleBack}
                onMakeOffer={handleMakeOffer}
                onBuyNow={handleBuyNow}
                isLoading={loading}
              />
            ) : (
              renderDefaultHeader(handleBack)
            )
          ) : null
        }
        mainContent={
          chatId ? (
            <div className="flex flex-col h-full min-h-0 bg-gray-50 dark:bg-neutral-950">
              {!isOnline && (
                <div className="mx-3 mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  It's Orrith, not you. You are offline, so messages may be delayed.
                </div>
              )}

              {notice && (
                <div className="mx-3 mt-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                  {notice}
                </div>
              )}

              <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-2 space-y-3">
                {messageLoadError && messages.length === 0 && (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
                    We could not load this conversation. Please check your connection and try again.
                  </div>
                )}
                {loading && messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="orrith-loader orrith-loader-sm orrith-loader-muted" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    {t('inbox:noMessages', 'No messages yet. Start the conversation!')}
                  </div>
                ) : (
                  <>
                    {/* Safety notice - embedded in chat stream, Orrith green, scrollable with messages */}
                    <div className="flex justify-center mb-2 px-2">
                      <div className="max-w-[min(92vw,26rem)] px-3 py-2 rounded-lg border border-green-200 dark:border-green-800/30 bg-green-50 dark:bg-green-950/10">
                        <p className="text-[11px] leading-relaxed text-green-800 dark:text-green-300">
                          {t('inbox:safetyNotice', 'Safety tip: Keep your conversations on the platform. Avoid sharing personal contact details.')}
                        </p>
                      </div>
                    </div>
                    {groupedMessages.map((item, idx) => {
                      if (item.type === 'date') {
                        return (
                          <div key={`date-${item.label}-${idx}`} className="text-center text-xs text-muted-foreground/70 py-1">
                            {item.label}
                          </div>
                        );
                      }
                      return (
                        <div
                          key={item.data.id}
                          className={`flex ${String(item.data.senderId) === String(currentUser.uid) ? 'justify-end' : 'justify-start'}`}
                        >
                          {renderMessageBubble(item.data)}
                        </div>
                      );
                    })}
                    {otherTyping && (
                      <div className="flex justify-start">
                        <div className="bg-card border border-border rounded-2xl rounded-bl-md px-3 py-2 text-sm text-muted-foreground italic">
                          {recipient?.displayName || 'User'} is typing...
                        </div>
                      </div>
                    )}
                    <div ref={bottomRef} />
                  </>
                )}
              </div>
            </div>
          ) : null
        }
        inputContent={
          chatId ? (
            <MessageInput
              onSend={handleSend}
              onTypingChange={handleTypingChange}
              placeholder={t('inbox:typeMessage', 'Type a message...')}
            />
          ) : null
        }
      />

      {showOfferModal && product && (
        <OfferModal
          product={product}
          sellerId={product.sellerId || product.uid}
          buyerId={currentUser?.uid}
          recipientId={counterToOfferId && offerCache[counterToOfferId]
            ? String(currentUser.uid) === String(offerCache[counterToOfferId].sellerId)
              ? offerCache[counterToOfferId].buyerId
              : offerCache[counterToOfferId].sellerId
            : null}
          chatId={chatId}
          counterToOfferId={counterToOfferId}
          onClose={() => {
            setShowOfferModal(false);
            setCounterToOfferId(null);
          }}
          onSuccess={() => setShowOfferModal(false)}
        />
      )}

      {showBuyNowModal && product && (
        <BuyNowGuidanceModal
          product={product}
          buyerId={currentUser?.uid}
          chatId={chatId}
          onClose={() => setShowBuyNowModal(false)}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        loading={confirmDialog.loading}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, title: '', message: '', confirmLabel: 'Confirm', onConfirm: null, loading: false })}
      />
    </>
  );
}